import * as React from "react";
import { useState } from "react";
import { ThumbsUp, ThumbsDown } from "lucide-react";
import { feedbackService } from "../../services/feedbackService";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { cn } from "@/lib/utils";

interface FeedbackButtonsProps {
  messageId: string;
  currentFeedback?: 'like' | 'dislike' | null;
  onFeedback: (messageId: string, feedback: 'like' | 'dislike') => void;
  inline?: boolean;
}

// Predefined reasons for negative feedback
const DISLIKE_REASONS = [
  'Did not follow my instructions',
  'Bad edit or low-quality changes',
  'Output too short / too long',
  'Tone or style incorrect',
  'Too slow',
  'Action failed or error occurred',
  'Other (optional comment)'
];

const FeedbackButtons: React.FC<FeedbackButtonsProps> = ({ 
  messageId, 
  currentFeedback, 
  onFeedback,
  inline = false
}) => {
  const [isDislikeDialogOpen, setIsDislikeDialogOpen] = useState(false);
  const [selectedReason, setSelectedReason] = useState<string | null>(null);
  const [comment, setComment] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleLike = async (e?: React.MouseEvent) => {
    e?.preventDefault();
    e?.stopPropagation();
    
    console.log('Submitting like feedback:', { messageId });
    
    // UI inmediata
    onFeedback(messageId, 'like');
    
    try {
      // Enviar a DB
      await feedbackService.submitFeedback({
        message_id: messageId,
        rating: 'like'
      });
      console.log('Like feedback submitted successfully');
    } catch (error) {
      console.error('Error saving like feedback:', error);
    }
  };

  const openDislikeDialog = () => {
    setIsDislikeDialogOpen(true);
  };

  const handleSubmitDislike = async (e?: React.MouseEvent) => {
    e?.preventDefault();
    e?.stopPropagation();
    
    if (!selectedReason) {
      console.warn('No reason selected');
      return;
    }

    console.log('Submitting dislike feedback:', { messageId, selectedReason, comment });
    setIsSubmitting(true);
    
    try {
      // 1. Actualizar UI principal (pone el pulgar rojo)
      onFeedback(messageId, 'dislike');
      
      // 2. Enviar datos detallados
      await feedbackService.submitFeedback({
        message_id: messageId,
        rating: 'dislike',
        reason: selectedReason,
        comment: selectedReason === 'Other (optional comment)' ? comment : undefined
      });

      console.log('Feedback submitted successfully');
      setIsDislikeDialogOpen(false);
      setSelectedReason(null);
      setComment("");
    } catch (error) {
      console.error('Error saving dislike feedback:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className={cn("flex items-center", inline ? "inline-flex" : "mt-1.5")}>
      <div className="flex gap-1 opacity-80 hover:opacity-100 transition-opacity">
        <Button 
          variant="ghost"
          size="icon"
          className={cn(
            "h-7 w-7 hover:bg-transparent",
            currentFeedback === 'like' ? "text-emerald-500 hover:text-emerald-600" : "text-gray-400 hover:text-gray-600"
          )}
          onClick={handleLike}
          title="Helpful"
          aria-label="Helpful"
        >
          <ThumbsUp className="w-4 h-4" />
        </Button>
        
        <Button 
          variant="ghost"
          size="icon"
          className={cn(
            "h-7 w-7 hover:bg-transparent",
            (currentFeedback === 'dislike' || isDislikeDialogOpen) ? "text-red-500 hover:text-red-600" : "text-gray-400 hover:text-gray-600"
          )}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            openDislikeDialog();
          }}
          title="Not helpful"
          aria-label="Not helpful"
        >
          <ThumbsDown className="w-4 h-4" />
        </Button>
      </div>

      <Dialog
        open={isDislikeDialogOpen}
        onOpenChange={(open) => {
          setIsDislikeDialogOpen(open);
          if (!open) {
            setSelectedReason(null);
            setComment("");
            setIsSubmitting(false);
          }
        }}
      >
        <DialogContent className="max-w-[520px]">
          <DialogHeader>
            <DialogTitle>What went wrong?</DialogTitle>
            <DialogDescription>
              This helps us improve response quality.
            </DialogDescription>
          </DialogHeader>

          <div className="text-xs">
            <div className="flex flex-col gap-1.5 max-h-[260px] overflow-y-auto pr-1">
              {DISLIKE_REASONS.map((reason) => (
                <label 
                  key={reason}
                  className={cn(
                    "flex items-center gap-2 cursor-pointer p-2 rounded transition-colors border",
                    selectedReason === reason ? "bg-indigo-50 border-indigo-200" : "hover:bg-muted border-transparent"
                  )}
                >
                  <input
                    type="radio"
                    name={`feedback_reason_${messageId}`}
                    value={reason}
                    checked={selectedReason === reason}
                    onChange={(e) => setSelectedReason(e.target.value)}
                    className="m-0 accent-indigo-600"
                  />
                  <span className="text-gray-700">{reason}</span>
                </label>
              ))}
            </div>

            {selectedReason === 'Other (optional comment)' && (
              <textarea
                value={comment}
                onChange={(e) => setComment(e.target.value)}
                placeholder="Tell us a bit more..."
                className="w-full mt-3 p-2 rounded border border-gray-300 text-xs min-h-[80px] focus:outline-none focus:ring-1 focus:ring-indigo-500 resize-none"
              />
            )}
          </div>

          <DialogFooter className="gap-2">
            <Button
              variant="ghost"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setIsDislikeDialogOpen(false);
              }}
              className="h-9"
            >
              Cancel
            </Button>
            <Button
              onClick={handleSubmitDislike}
              disabled={!selectedReason || isSubmitting}
              className={cn(
                "h-9 bg-emerald-600 hover:bg-emerald-700 text-white",
                (!selectedReason || isSubmitting) && "opacity-50 cursor-not-allowed"
              )}
            >
              {isSubmitting ? 'Sending...' : 'Submit'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default FeedbackButtons;






